// Adapter layer — maps real WorkOrder type to the shapes the view components expect.
// The views were built with a DummyWorkOrder shape; this bridges the gap without
// rewriting every view component.

import type { WorkOrder, WorkOrderStatus, WorkOrderPriority } from '@cmms/types'

export type WoStatus = WorkOrderStatus
export type WoPriority = WorkOrderPriority

export interface Assignee {
  id: string
  name: string
  initials: string
  color: string
  avatarUrl?: string | null
}

export interface DummyWorkOrder {
  id: string
  title: string
  description: string
  status: WoStatus
  priority: WoPriority
  type: string
  assignees: Assignee[]
  dueDate: Date
  startDate: Date | null
  createdAt: Date
  startedAt?: Date
  completedAt?: Date
  asset?: { name: string; code: string }
  location: string
  categories: string[]
  estimatedHours: number
  progress?: number
  commentCount: number
  attachmentCount: number
  _raw: WorkOrder
}

const AVATAR_COLORS = ['#0D9488', '#D97706', '#2563EB', '#DC2626', '#7C3AED', '#EA580C', '#059669', '#DB2777', '#0E7490', '#65A30D']

function userColor(id: string) {
  let hash = 0
  for (const c of id) hash = ((hash << 5) - hash + c.charCodeAt(0)) | 0
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!
}

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2)
}

export function adaptWorkOrder(wo: WorkOrder): DummyWorkOrder {
  // Multi-user assignment: prefer the aggregated `assignees` array,
  // fall back to the legacy singular pointer for old rows created
  // before the backfill ran. Team-only assignment (no users) still
  // renders the team avatar as a single "assignee" tile so the row
  // isn't shown as unassigned.
  const assignees: Assignee[] = []
  const seen = new Set<string>()
  for (const a of wo.assignees ?? []) {
    if (!a?.id || seen.has(a.id)) continue
    seen.add(a.id)
    assignees.push({
      id: a.id,
      name: a.name,
      initials: initials(a.name),
      color: userColor(a.id),
      avatarUrl: a.avatar_url,
    })
  }
  if (assignees.length === 0 && wo.assigned_to_user_id && wo.assigned_to_user_name) {
    assignees.push({
      id: wo.assigned_to_user_id,
      name: wo.assigned_to_user_name,
      initials: initials(wo.assigned_to_user_name),
      color: userColor(wo.assigned_to_user_id),
      avatarUrl: wo.assigned_to_user_avatar,
    })
  }
  if (wo.assigned_to_team_id && wo.assigned_to_team_name && !assignees.length) {
    assignees.push({
      id: wo.assigned_to_team_id,
      name: wo.assigned_to_team_name,
      initials: initials(wo.assigned_to_team_name),
      color: '#7C3AED',
    })
  }

  return {
    id: wo.id,
    title: wo.title,
    description: wo.description ?? '',
    status: wo.status,
    priority: wo.priority,
    type: 'reactive',
    assignees,
    dueDate: wo.due_date ? new Date(wo.due_date) : new Date(),
    startDate: wo.start_date ? new Date(wo.start_date) : null,
    createdAt: new Date(wo.created_at),
    startedAt: wo.status === 'in_progress' ? new Date(wo.updated_at) : undefined,
    completedAt: wo.completed_at ? new Date(wo.completed_at) : undefined,
    asset: wo.asset_name ? { name: wo.asset_name, code: wo.asset_code ?? '' } : undefined,
    location: [wo.site_name, wo.location_name].filter(Boolean).join(' · '),
    categories: [],
    estimatedHours: wo.estimated_minutes ? Math.round(wo.estimated_minutes / 60 * 10) / 10 : 0,
    progress: wo.status === 'in_progress' ? 50 : (wo.status === 'closed' || wo.status === 'resolved') ? 100 : 0,
    commentCount: 0,
    attachmentCount: 0,
    _raw: wo,
  }
}

export function adaptWorkOrders(orders: WorkOrder[]): DummyWorkOrder[] {
  return orders.map(adaptWorkOrder)
}

export const STATUS_META: Record<string, { label: string; color: string; bg: string }> = {
  open:        { label: 'Open',        color: '#3B82F6', bg: '#EFF6FF' },
  in_progress: { label: 'In Progress', color: '#D97706', bg: '#FFFBEB' },
  closed:      { label: 'Closed',      color: '#8B5CF6', bg: '#F5F3FF' },
  resolved:    { label: 'Resolved',    color: '#059669', bg: '#ECFDF5' },
  scheduled:   { label: 'Scheduled',   color: '#0EA5E9', bg: '#F0F9FF' },
}

/// Module-aware status meta. Work-order flow calls terminal-success
/// "Resolved" (aligned with ticket workflows / SLA vocabulary), while
/// the Tasks module calls the same underlying status "Completed" —
/// that's the language operators use for housekeeping / hospitality
/// checklists. Everything else (colours, other statuses) stays
/// identical between the two modules.
export function getStatusMeta(
  status: string,
  isTask?: boolean,
): { label: string; color: string; bg: string } {
  const base = STATUS_META[status] ?? { label: status, color: '#64748B', bg: '#F8FAFC' }
  if (isTask && status === 'resolved') return { ...base, label: 'Completed' }
  return base
}

export const PRIORITY_META: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: 'Critical', color: '#DC2626', bg: '#FEF2F2' },
  high:     { label: 'High',     color: '#EA580C', bg: '#FFF7ED' },
  medium:   { label: 'Medium',   color: '#D97706', bg: '#FFFBEB' },
  low:      { label: 'Low',      color: '#059669', bg: '#ECFDF5' },
}

export const TYPE_LABEL: Record<string, string> = {
  reactive:    'Reactive',
  preventive:  'Preventive',
  inspection:  'Inspection',
  safety:      'Safety',
  other:       'Other',
}

// Same palette as DetailedOverview.tsx's WORK_TYPE_COLORS (kept in sync
// by hand — both map the same work_type enum to the same colors for a
// consistent legend across the app).
export const TYPE_COLOR: Record<string, string> = {
  reactive:    '#EF4444',
  preventive:  '#10B981',
  inspection:  '#0EA5E9',
  safety:      '#F59E0B',
  other:       '#94A3B8',
}

export const ASSIGNEES: Assignee[] = []
export const WORK_ORDERS: DummyWorkOrder[] = []
